import React, { useState, useRef, useCallback, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { TextField, MenuItem, Box, Typography, FormControlLabel, Tooltip, Button } from "@mui/material";
import {
  Radio,
  RadioGroup,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { apiClient } from "../../../../globals/utils/api";
import CreatableSelect from "react-select/creatable";
import { FixedSizeList as List } from "react-window";
import { Info } from "@mui/icons-material";
import { loadGoogleMaps } from "../../../../globals/utils/googleMapsLoader";

// Custom dropdown styling (matches MUI styles)
const customStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "transparent", // Make the background transparent
    minHeight: "56px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    boxShadow: "none",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for dropdown menu
    fontSize: "16px", // Decrease font size
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for menu portal
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#6200ea' : state.isFocused ? '#f0f0f0' : '#fff',
    color: state.isSelected ? '#fff' : '#000',
    fontSize: "16px", // Decrease font size
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  }),
};

// Labeled CreatableSelect Component
const LabeledCreatableSelect = ({ label, options, value, onChange, placeholder }) => {
  return (
    <Box sx={{ marginBottom: "2px" }}>
      {/* Label styled to mimic MUI's TextField label */}
      <Typography
        sx={{
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.6)",
          marginBottom: "4px",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <CreatableSelect
        isClearable
        options={options}
        components={{ MenuList }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        styles={customStyles}
      />
    </Box>
  );
};

// Virtualized MenuList for better performance
const MenuList = (props) => {
  const height = 35;
  const { options, children, maxHeight } = props;

  return (
    <List
      height={Math.min(maxHeight, options.length * height)}
      itemCount={children.length}
      itemSize={height}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};


// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { fields: ["address_components"], types: ["geocode"] }
      );
  
      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();
  
        if (!place || !place.address_components) {
          console.error("No valid place returned or missing address components.");
          return;
        }
  
        let address = "", city = "", state = "", zipCode = "";
  
        place.address_components.forEach((component) => {
          const type = component.types[0];
          switch (type) {
            case "street_number":
              address = `${component.long_name} ${address}`;
              break;
            case "route":
              address += component.long_name;
              break;
            case "locality":
              city = component.long_name;
              break;
            case "administrative_area_level_1":
              state = component.long_name;
              break;
            case "postal_code":
              zipCode = component.long_name;
              break;
            default:
              break;
          }
        });

  
        if (callback) {
          callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);
  

  return (
    <TextField
  variant="outlined"
  fullWidth
  placeholder={placeholder}
  inputRef={inputRef}
  sx={{ marginBottom: "8px" }} // Add margin-bottom
/>
  );
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Smooth scrolling
  });
};


export const BillingInformationForm = ({ data, updateData, globalData, isGuest }) => {
    // Check if any guardian has non-USA citizenship
    const hasForeignCitizenship = globalData.guardianDetails?.guardians?.some(
      guardian => 
        (guardian.citizenship && 
         guardian.citizenship.toLowerCase() !== 'usa' && 
         guardian.citizenship.toLowerCase() !== 'united states') 
        || 
        (guardian.custom_citizenship && 
         guardian.custom_citizenship.toLowerCase() !== 'usa' && 
         guardian.custom_citizenship.toLowerCase() !== 'united states')
    );

    // Check if any PG has preferred_billing === true
    const hasPGPreferredBilling = globalData.guardianDetails?.guardians?.some(
      guardian => guardian.preferred_billing === true
    );

    const buildBillingPayload = useCallback((info) => {
      return {
        billing_entity: info.billing_entity,
        billing_lawfirmaddress_name:
          info.billing_entity === "Law Firm" ? (info.billing_lawfirmaddress_name || "") : "",
        billing_principal_name:
          info.billing_entity === "Principal" ? (info.billing_principal_name || "") : "",
        billing_other_name: info.billing_entity === "Other" ? (info.billing_other_name || "") : "",
        billing_other_address: info.billing_entity === "Other" ? (info.billing_other_address || "") : "",
        billing_other_relationship:
          info.billing_entity === "Other" ? (info.billing_other_relationship || "") : "",
        billing_other_city: info.billing_entity === "Other" ? (info.billing_other_city || "") : "",
        billing_other_state: info.billing_entity === "Other" ? (info.billing_other_state || "") : "",
        billing_other_zip: info.billing_entity === "Other" ? (info.billing_other_zip || "") : "",
        is_billing_override: info.is_billing_override ?? false,
      };
    }, []);

    const initialBillingEntity =
      isGuest || hasForeignCitizenship
        ? "Law Firm"
        : hasPGPreferredBilling
        ? "Principal"
        : (globalData?.attorneyInformation?.attorneyInvolved === "Yes" ? "Law Firm" : "Principal");

    const [billingInfo, setBillingInfo] = useState({
      billing_entity: initialBillingEntity,
      billing_lawfirmaddress_name: "",
      billing_principal_name: "",
      billing_other_name: "",
      billing_other_address: "",
      billing_other_relationship: "",
      billing_other_city: "",
      billing_other_state: "",
      billing_other_zip: "",
      is_billing_override: false,
    });

    const [billingOverrideModalOpen, setBillingOverrideModalOpen] = useState(false);
    const [pendingBillingEntity, setPendingBillingEntity] = useState(null);
  
    const [principalOptions, setPrincipalOptions] = useState([]);
    const [lawFirmLocations, setLawFirmLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allCities, setAllCities] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);

    useEffect(()=>{
      console.log(globalData);
    }, [globalData])
  
    // Fetch principal options and law firm locations on component mount
    useEffect(() => {
      const fetchPrincipalOptions = () => {
        setLoading(true);
        try {
        const principals = globalData.guardianDetails?.guardians?.map((guardian) => ({
          label: guardian.name + " - " + guardian.address + ", " + guardian.city + ", " + guardian.state + ", " + guardian.zip_code,
          value: guardian.name,
        }));
        setPrincipalOptions(principals || []);
      } catch (error) {
        console.error("Error fetching principals:", error);
      } finally {
        setLoading(false);
      }
      };
  
      const fetchLawFirmLocations = async () => {
        setLoading(true);
        try {
          // Guest flow: build a single pseudo-location from guest law firm name/address
          if (isGuest) {
            const guestName = globalData?.attorneyInformation?.guest_lawfirm_name || "Guest Law Firm";
            const guestAddress = globalData?.attorneyInformation?.guest_lawfirmaddress_name || "";
            const formattedLocations = [
              {
                label: guestName,
                value: guestAddress || guestName,
                address: guestAddress,
              },
            ];
            setLawFirmLocations(formattedLocations);
            return;
          }

          if (globalData.attorneyInformation?.lawFirm) {
            const response = await apiClient(
              `/get-locations-by-lawfirm/${globalData.attorneyInformation.lawFirm}`,
              "GET"
            );
          const formattedLocations = response.map((location) => ({
            label: location.name + " - " + location.address,
            value: location.law_firm_location_id,
            address: location.address,
          }));
          setLawFirmLocations(formattedLocations);
        }
        } catch (error) {
          console.error("Error fetching law firm locations:", error);
        } finally {
          setLoading(false);
        }
      };

      const fetchCitiesAndStates = async () => {
        try {
          const cities = await apiClient("/fields/city/", "GET");
          const states = await apiClient("/fields/states/", "GET");
          setAllCities(cities);
          setAllStates(states);
        } catch (error) {
          console.error("Error loading cities or states:", error);
        } finally {
          setLoadingCitiesStates(false);
        }
      };
  
      fetchPrincipalOptions();
      fetchLawFirmLocations();
      fetchCitiesAndStates();
    }, [globalData.guardianDetails, globalData.attorneyInformation, isGuest]);

    // Force Law Firm billing for foreign citizenship flows (and disable selector in UI)
    useEffect(() => {
      if (hasForeignCitizenship && billingInfo.billing_entity !== "Law Firm") {
        // Prefer the already-selected attorney law firm address (if present) to avoid extra clicks
        const preferredLocation =
          globalData?.attorneyInformation?.lawFirmAddress ||
          billingInfo.billing_lawfirmaddress_name ||
          "";
        const next = {
          ...billingInfo,
          billing_entity: "Law Firm",
          billing_lawfirmaddress_name: preferredLocation,
        };
        setBillingInfo(next);
        updateData(buildBillingPayload(next));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasForeignCitizenship]);

    useEffect(() => {
      if (billingInfo.billing_entity === "Law Firm") {
        if (lawFirmLocations.length === 1) {
          const selectedLocation = lawFirmLocations[0].value;
          setBillingInfo((prevState) => ({
            ...prevState,
            billing_lawfirmaddress_name: selectedLocation,
          }));
    
          updateData(
            {
              billing_entity: "Law Firm",
              billing_lawfirmaddress_name: selectedLocation
            });
        }
      } else if (billingInfo.billing_entity === "Principal") {
        if (principalOptions.length === 1) {
          const selectedPrincipal = principalOptions[0].value;
          setBillingInfo((prevState) => ({
            ...prevState,
            billing_principal_name: selectedPrincipal,
          }));
    
          updateData(
            {
              billing_entity: "Principal",
              billing_principal_name: selectedPrincipal
            });
        }
      }
    }, [billingInfo.billing_entity, principalOptions, lawFirmLocations, updateData]);    

    // Sync form state with incoming data
    useEffect(() => {
      if (data) {
        setBillingInfo((prevState) => ({
          ...prevState,
          billing_entity: data.billing_entity || prevState.billing_entity,
          billing_lawfirmaddress_name: data.billing_lawfirmaddress_name || prevState.billing_lawfirmaddress_name,
          billing_principal_name: data.billing_principal_name || prevState.billing_principal_name,
          billing_other_name: data.billing_other_name || prevState.billing_other_name,
          billing_other_address: data.billing_other_address || prevState.billing_other_address,
          billing_other_relationship: data.billing_other_relationship || prevState.billing_other_relationship,
          billing_other_city: data.billing_other_city || prevState.billing_other_city,
          billing_other_state: data.billing_other_state || prevState.billing_other_state,
          billing_other_zip: data.billing_other_zip || prevState.billing_other_zip,
        }));
      }
    }, [data]);
  
    // Handle form field changes and update state dynamically
    const handleChange = (e) => {
      const { name, value } = e.target;

      // If PG preferred billing applies and user tries to switch to "Law Firm", show confirmation
      if (name === "billing_entity" && value === "Law Firm" && hasPGPreferredBilling && !hasForeignCitizenship) {
        setPendingBillingEntity("Law Firm");
        setBillingOverrideModalOpen(true);
        return;
      }

      const updatedBillingInfo = {
        ...billingInfo,
        [name]: value,
        ...(name === "billing_entity" && { is_billing_override: false }),
      };
      setBillingInfo(updatedBillingInfo);
      updateData(buildBillingPayload(updatedBillingInfo));
    };

    const handleOverrideConfirm = () => {
      const updatedBillingInfo = {
        ...billingInfo,
        billing_entity: pendingBillingEntity,
        is_billing_override: true,
      };
      setBillingInfo(updatedBillingInfo);
      updateData(buildBillingPayload(updatedBillingInfo));
      setBillingOverrideModalOpen(false);
      setPendingBillingEntity(null);
    };

    const handleOverrideCancel = () => {
      setBillingOverrideModalOpen(false);
      setPendingBillingEntity(null);
    };
  
    useEffect(() => {
      scrollToTop();
    }, []);
  
    return (
      <Box
        className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
        sx={{
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Heading */}
        <Typography variant="h5" className="font-bold mb-2">
          Billing Information
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "text.secondary" }}
        >
          Please select the billing address as this is where Liberty will be
          mailing the invoice. It will take 7 to 10 days, and payment online
          will not be available for 2 to 3 business days.
        </Typography>
  
        {/* Question: Who to Bill? */}
        <Box sx={{ mb: 4 }}>
          {hasForeignCitizenship && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
              <Info sx={{ color: '#856404', mr: 1 }} />
              <Typography variant="body2" sx={{ color: '#856404' }}>
                Liberty only allows Agency Billing for foreign customers. Law Firm billing has been automatically selected.
              </Typography>
            </Box>
          )}
          {hasPGPreferredBilling && !hasForeignCitizenship && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
              <Info sx={{ color: '#1565c0', mr: 1 }} />
              <Typography variant="body2" sx={{ color: '#1565c0' }}>
                This professional guardian prefers Principal billing. Principal has been automatically selected. Switching to Law Firm will require confirmation.
              </Typography>
            </Box>
          )}
          
          <Tooltip 
            title={hasForeignCitizenship ? "Liberty only allows Agency Billing for foreign customers" : ""}
            placement="top"
          >
            <TextField
              select
              label="Who to Bill?"
              name="billing_entity"
              value={billingInfo.billing_entity}
              onChange={handleChange}
              fullWidth
              required
              disabled={hasForeignCitizenship}
            >
              <MenuItem value="Law Firm">Law Firm</MenuItem>
              <MenuItem 
                value="Principal" 
                disabled={hasForeignCitizenship}
                sx={hasForeignCitizenship ? { opacity: 0.5 } : {}}
              >
                Principal
              </MenuItem>
              <MenuItem 
                value="Other" 
                disabled={hasForeignCitizenship}
                sx={hasForeignCitizenship ? { opacity: 0.5 } : {}}
              >
                Other
              </MenuItem>
            </TextField>
          </Tooltip>
        </Box>
  
        {/* Billing Locations for Law Firm */}
        {billingInfo.billing_entity === "Law Firm" && (
          <FormControl component="fieldset" sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select Billing Location:
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <RadioGroup
                name="billing_lawfirmaddress_name"
                value={billingInfo.billing_lawfirmaddress_name}
                onChange={handleChange}
              >
                {lawFirmLocations.map((location) => (
                  <FormControlLabel
                    key={location.value}
                    value={location.value}
                    control={<Radio />}
                    label={`${location.label} - ${location.address}`}
                  />
                ))}
              </RadioGroup>
            )}
          </FormControl>
        )}
  
        {/* Billing Locations for Principal */}
        {billingInfo.billing_entity === "Principal" && (
          <FormControl component="fieldset" sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select Principal:
            </Typography>
            <RadioGroup
              name="billing_principal_name"
              value={billingInfo.billing_principal_name}
              onChange={handleChange}
            >
              {principalOptions.map((principal) => (
                <FormControlLabel
                  key={principal.value}
                  value={principal.value}
                  control={<Radio />}
                  label={principal.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
  
        {/* Billing Override Confirmation Modal */}
        <Dialog open={billingOverrideModalOpen} onClose={handleOverrideCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Override Preferred Billing</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This professional guardian prefers Principal billing. Are you sure you want to switch to Law Firm billing? This will be recorded as a billing override.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOverrideCancel} color="primary">Cancel — Keep Principal</Button>
            <Button onClick={handleOverrideConfirm} variant="contained" color="warning">
              Confirm — Switch to Law Firm
            </Button>
          </DialogActions>
        </Dialog>

        {/* If "Other" is selected, show additional fields */}
        {billingInfo.billing_entity === "Other" && (
          <>
            <TextField
              label="Name"
              name="billing_other_name"
              value={billingInfo.billing_other_name}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
  
            <TextField
              label="Relationship"
              name="billing_other_relationship"
              value={billingInfo.billing_other_relationship}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <Typography
              sx={{
                fontSize: "12px",
                color: "rgba(0, 0, 0, 0.6)",
                marginBottom: "4px",
                fontWeight: 500,
              }}
            >
              Search Billing Address
            </Typography>

            <GoogleAutoComplete
              placeholder="Search Billing Address"
              callback={(address, city, state, zipCode) => {
                setBillingInfo(prev => ({
                  ...prev,
                  billing_other_address: address,
                  billing_other_city: city,
                  billing_other_state: state,
                  billing_other_zip: zipCode,
                }));
                updateData({
                  ...data,
                  billing_other_address: address,
                  billing_other_city: city,
                  billing_other_state: state,
                  billing_other_zip: zipCode,
                });
              }}
            />

            <TextField
              label="Street Address"
              name="billing_other_address"
              value={billingInfo.billing_other_address || ""}
              onChange={handleChange}
              fullWidth
              sx={{ mt: 1, mb: 2 }}
            />

            <TextField
              label="ZIP Code"
              name="billing_other_zip"
              value={billingInfo.billing_other_zip || ""}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />

            {loadingCitiesStates ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <LabeledCreatableSelect
                  label="City"
                  options={allCities.map((city) => ({ label: city, value: city }))}
                  value={billingInfo.billing_other_city ? { label: billingInfo.billing_other_city, value: billingInfo.billing_other_city } : null}
                  onChange={(opt) => {
                    setBillingInfo(prev => ({
                      ...prev,
                      billing_other_city: opt ? opt.value : "",
                    }));
                    updateData({
                      ...data,
                      billing_other_city: opt ? opt.value : "",
                    });
                  }}
                  placeholder="Select or add a city"
                />

                <LabeledCreatableSelect
                  label="State"
                  options={allStates.map((state) => ({ label: state, value: state }))}
                  value={billingInfo.billing_other_state ? { label: billingInfo.billing_other_state, value: billingInfo.billing_other_state } : null}
                  onChange={(opt) => {
                    setBillingInfo(prev => ({
                      ...prev,
                      billing_other_state: opt ? opt.value : "",
                    }));
                    updateData({
                      ...data,
                      billing_other_state: opt ? opt.value : "",
                    });
                  }}
                  placeholder="Select or add a state"
                />
              </>
            )}
          </>
        )}
      </Box>
    );
};